/**
 * 消息卡片组合式函数
 * 处理内容展开/收起逻辑
 */

import { ref, nextTick, type Ref } from 'vue'

export interface UseMessageCardOptions {
    /** 最大显示行数 */
    maxLines?: number
    /** 是否启用展开功能 */
    enableExpansion?: boolean
}

export function useMessageCard(contentRef: Ref<HTMLElement | undefined>, options: UseMessageCardOptions = {}) {
    const { maxLines = 3, enableExpansion = true } = options

    // 响应式状态
    const isExpanded = ref(false)
    const needsExpansion = ref(false)
    const isChecking = ref(false)

    /**
     * 检测内容是否需要展开功能
     */
    const checkNeedsExpansion = async (): Promise<boolean> => {
        if (!contentRef.value || !enableExpansion) {
            needsExpansion.value = false
            return false
        }

        isChecking.value = true

        try {
            await nextTick()

            const element = contentRef.value

            // 创建一个临时克隆元素来测量实际高度
            const tempElement = element.cloneNode(true) as HTMLElement
            tempElement.style.position = 'absolute'
            tempElement.style.visibility = 'hidden'
            tempElement.style.height = 'auto'
            tempElement.style.maxHeight = 'none'
            tempElement.style.webkitLineClamp = 'unset'
            tempElement.style.overflow = 'visible'

            // 添加到DOM中进行测量
            element.parentNode?.appendChild(tempElement)

            const computedStyle = getComputedStyle(element)
            const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5
            const maxHeight = lineHeight * maxLines
            const actualHeight = tempElement.scrollHeight

            // 移除临时元素
            element.parentNode?.removeChild(tempElement)

            const needs = actualHeight > maxHeight + 5 // 5px 容错
            needsExpansion.value = needs

            return needs
        } catch (error) {
            needsExpansion.value = false
            return false
        } finally {
            isChecking.value = false
        }
    }

    /**
     * 使用 ResizeObserver 监听内容变化
     */
    const observeContentChanges = () => {
        if (!contentRef.value || typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(() => {
            checkNeedsExpansion()
        })

        observer.observe(contentRef.value)

        return () => {
            observer.disconnect()
        }
    }

    /**
     * 切换展开/收起状态
     */
    const toggleExpansion = () => {
        if (!needsExpansion.value) return

        isExpanded.value = !isExpanded.value
        return isExpanded.value
    }

    /**
     * 展开内容
     */
    const expand = () => {
        if (needsExpansion.value) {
            isExpanded.value = true
        }
    }

    /**
     * 收起内容
     */
    const collapse = () => {
        isExpanded.value = false
    }

    /**
     * 重置状态
     */
    const reset = () => {
        isExpanded.value = false
        needsExpansion.value = false
    }

    /**
     * 获取内容样式类
     */
    const getContentClasses = () => {
        return {
            'message-content-collapsed': !isExpanded.value && needsExpansion.value,
            'message-content-expanded': isExpanded.value,
            'message-content-checking': isChecking.value,
        }
    }

    return {
        // 状态
        isExpanded: readonly(isExpanded),
        needsExpansion: readonly(needsExpansion),
        isChecking: readonly(isChecking),

        // 方法
        checkNeedsExpansion,
        observeContentChanges,
        toggleExpansion,
        expand,
        collapse,
        reset,
        getContentClasses,
    }
}
